/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  Spinner,
  NavLink,
  CardTable as ClientListTable
} from '../../components'

import {
  decodeBase64,
  PageNavigationControl
} from '../../utils'


class PayrollCycleClients extends React.Component {
  static propTypes = {
    payrollCycle: PropTypes.string.isRequired
  }

  constructor(props) {
    super(props)

    this.state = {
      onFirstPage: true,
      onLastPage: false,
      payrollCycle: decodeBase64(this.props.payrollCycle),
      refetch: false,
      pageNav: new PageNavigationControl()
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.error && !this.state.refetch) {
      this.setState({
        refetch: true
      })
      nextProps.refetch()
    }

    if (this.state.refetch) {
      this.setState({
        refetch: false
      })
    }

    if (nextProps.clients) {
      this.state.pageNav.setPageStates(this.props.clients, nextProps.clients)
    }
  }

  generateTableDataRows = (dataRows) => {
    let rows = dataRows.map((row, i) => (
      <ClientListTableDataRow data={row} key={i}/>
    ))

    return rows
  }

  render() {
    if (this.props.loading) {
      return <Spinner />
    }

    if (this.props.error) {
      return <div>We&rsquo;re experiencing a data loading problem</div>
    }

    let rows = this.generateTableDataRows(this.props.clients.edges)
    let pageNav = this.state.pageNav

    return (
      <div>
        <ClientListTable
          heading={"Clients"}
          headingIcon="groups"
          tableColumnHeader={<ClientListTableHeaderRow />}
          tableDataRows={rows}
          nextPage={() => pageNav.nextPage(this.props.loadNextClients, this.props.payrollCycle)}
          previousPage={() => pageNav.previousPage(this.props.loadPreviousClients, this.props.payrollCycle)}
          onFirstPage={pageNav.firstPage}
          onLastPage={pageNav.lastPage}
        />
      </div>
    )
  }
}

const ClientListTableHeaderRow = () => {
  return (
    <tr className="slds-line-height--reset">
      <th className="slds-text-title--caps" scope="col">
        <span className="slds-truncate slds-th__action" title="First name">First name</span>
      </th>
      <th className="slds-text-title--caps" scope="col">
        <span className="slds-truncate" title="Last name">Last name</span>
      </th>
      <th className="slds-text-title--caps" scope="col">
        <span className="slds-truncate" title="Other name">Other name</span>
      </th>
      <th className="slds-text-title--caps" scope="col">
        <span className="slds-truncate" title="Identity number">Identity number</span>
      </th>
      <th className="slds-text-title--caps" scope="col">
        <span className="slds-truncate" title="Employer / Division">Employer / Division</span>
      </th>
    </tr>
  )
}

const ClientListTableDataRow = (props) => {
  let row = props.data.node

  return (
    <tr className="slds-hint-parent">
      <th scope="row">
        <div className="slds-truncate" title={row.identity.firstName}>
          <NavLink to={'/os/identity/' + row.identity.id} >
            {row.identity.firstName}
          </NavLink>
        </div>
      </th>
      <td role="gridcell">
        <div className="slds-truncate" title={row.identity.lastName}>
          {row.identity.lastName}
        </div>
      </td>
      <td role="gridcell">
        <div className="slds-truncate" title={row.identity.otherName}>
          {row.identity.otherName}
        </div>
      </td>
      <td role="gridcell">
        <div className="slds-truncate" title={row.identity.identityValue}>
          {row.identity.identityValue}
        </div>
      </td>
      <td role="gridcell">
        <div className="slds-truncate" title={row.division.employer.friendlyName}>
          <NavLink to={"/os/identity/"+row.division.employer.identity.id} >
            {row.division.employer.friendlyName+' - '+row.division.name}
          </NavLink>
        </div>
      </td>
    </tr>
  )
}

const getPayrollCycleClients = gql`query($id: UUID, $cursor: String) {
    clients(payrollcycle_Id: $id, first: 15, after: $cursor, orderBy: "identity__last_name") {
      edges {
        node {
          ...ClientInformation
          division {
            id
            name
            employer {
              id
              friendlyName
              identity {
                id
              }
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.clientInformation}
`

const getPreviousPayrollCycleClients = gql`query($id: UUID, $cursor: String) {
    clients(payrollcycle_Id: $id, last: 15, before: $cursor, orderBy: "identity__last_name") {
      edges {
        node {
          ...ClientInformation
          division {
            id
            name
            employer {
              id
              friendlyName
              identity {
                id
              }
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.clientInformation}
`

const PayrollCycleClientsWithData =
  graphql(getPayrollCycleClients, {
    options: (ownProps) => ({
      variables: {
        id: decodeBase64(ownProps.payrollCycle)
      }
    }),
    props: ({ data: { loading, error, refetch, clients, fetchMore } }) => ({
      loading,
      error,
      refetch,
      clients,
      loadNextClients: (payrollCycle) => {
        return fetchMore({
          query: getPayrollCycleClients,
          variables: {
            cursor: clients.pageInfo.endCursor,
            id: decodeBase64(payrollCycle)
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.clients.edges
            const pageInfo = fetchMoreResult.clients.pageInfo

            return {
              clients: {
                __typename: previousResult.clients.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      loadPreviousClients: (payrollCycle) => {
        return fetchMore({
          query: getPreviousPayrollCycleClients,
          variables: {
            cursor: clients.pageInfo.startCursor,
            id: decodeBase64(payrollCycle)
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.clients.edges
            const pageInfo = fetchMoreResult.clients.pageInfo

            return {
              clients: {
                __typename: previousResult.clients.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })(PayrollCycleClients)

export default PayrollCycleClientsWithData



// WEBPACK FOOTER //
// ./src/components/payroll-cycle-clients/index.js